<template>
    <div class="smr" :class="[{'is-sidebar-toggled': sidebarToggled}, {'is-sidebar-moving-touch': this.sidebarMovingWithTouch}, {'is-sidebar-moving-transition': this.sidebarMovingWithTransition}, this.mobile ? `is-mobile-sidebar-size--${mobileSidebarHeightMode}` : '']">
        <div class="smr__sidebar" :style="{height: computedMobileSidebarHeight}">
            <div class="smr__sidebar__inner">
                <Sidebar />
            </div>
            <SmrTooltip :content="sidebarToggleTooltip" position="right">
                <div class="smr__sidebar__toggle" v-touch:swipe.left="sidebarToggleSwipeLeftHandler" @click="toggleSidebar" v-touch:swipe.right="sidebarToggleSwipeRightHandler">
                    <Icon class="smr__sidebar__toggle__icon" :size="20">
                        <IconArrowFilledLeft />
                    </Icon>
                </div>
            </SmrTooltip>
        </div>
        <div class="smr__map">
            <Map :tabletSidebarToggled="sidebarToggled"/>
            <FuelLogos v-if="showFuelLogos" />
        </div>
        <div class="smr__mobile-search" v-if="mobile && mobileSearchVisible">
            <MobileSearch :waypoint-index="mobileSearchWaypointIndex" :search-value="mobileSearchValue"/>
        </div>
        <GeneralSettings />
        <FavouritePanel/>
        <FavouritesListPanel/>
        <MapboxCookieInfo />
        <FeatureIntroduction />
        <DisclaimerInfo />
        <POIEditPrices/>
        <ThankYouMessage/>
        <PrintPreview />
        <PackingList v-if="isMAPage" />
        <EmailSharingForm v-if="isMAPage" />
        <SuccessMessage />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { trackEvent, trackGa4Event } from '@/utils/tracking'
import messageBoxBlueprint from '@/mixins/message-box-blueprint.js';
import Sidebar from '@/components/Sidebar.vue';
import Map from '@/components/Map.vue';
import GeneralSettings from '@/components/GeneralSettings.vue';
import FavouritePanel from '@/components/FavouritePanel.vue';
import FavouritesListPanel from '@/components/FavouritesListPanel.vue';
import FeatureIntroduction from '@/components/FeatureIntroduction.vue';
import DisclaimerInfo from '@/components/DisclaimerInfo.vue';
import Icon from '@/components/Icon.vue'
import IconArrowFilledLeft from '@/components/icons/IconArrowFilledLeft.vue'
import eventBus from '@/utils/event-bus';
import POIEditPrices from '../components/POIEditPrices.vue';
import ThankYouMessage from '../components/ThankYouMessage.vue'
import PrintPreview from '../components/PrintPreview.vue'
import SmrTooltip from '../components/SmrTooltip.vue'
import FuelLogos from '@/components/FuelLogos.vue'
import MapboxCookieInfo from '@/components/MapboxCookieInfo.vue'
import PackingList from '@/components/PackingList.vue'
import EmailSharingForm from '@/components/EmailSharingForm.vue'
import SuccessMessage from '@/components/SuccessMessage.vue'

export default {
    components: {
        Sidebar,
        Map,
        MobileSearch: () => import(/* webpackChunkName: "MobileSearch" */ '@/components/MobileSearch.vue'),
        GeneralSettings,
        FavouritePanel,
        FavouritesListPanel,
        FeatureIntroduction,
        DisclaimerInfo,
        Icon,
        IconArrowFilledLeft,
        POIEditPrices,
        ThankYouMessage,
        PrintPreview,
        SmrTooltip,
        FuelLogos,
        MapboxCookieInfo,
        PackingList,
        EmailSharingForm,
        SuccessMessage,
    },
    mixins: [messageBoxBlueprint],
    data() {
        return {
            mobileSearchWaypointIndex: 0,
            mobileSearchValue: '',
            sidebarToggled: false, // for tablet view toggle
            sidebarMovingPosition: 0,
            mobileSidebarMovingDelta: 0,
            sidebarMovingWithTouch: false,
            sidebarMovingWithTransition: false,
        }
    },
    computed: {
        ...mapGetters(['mobile', 'mobileSidebarHeightMode', 'mobileSidebarHeight', 'selectedPOIs', 'mobileSearchVisible', 'isMAPage']),
        computedMobileSidebarHeight() {
            if(this.mobile) {
                return `calc(${this.mobileSidebarHeight} + ${this.mobileSidebarMovingDelta}px)`;
            } else {
                return 'auto';
            }
        },
        showFuelLogos() {
            return this.selectedPOIs.includes('gasStations') ||  this.selectedPOIs.includes('chargingStations')
        },
        sidebarToggleTooltip() {
            return this.sidebarToggled ? 'Menü einblenden' : 'Menü ausblenden'
        }
    },
    created() {
        this.addListeners();
    },
    beforeDestroy() {
        this.removeListeners()
    },
    methods: {
        sidebarHeaderSwipeUpHandler() {
            this.clearMovingVariables();
            if(!this.mobile) return false;
            if(this.mobileSidebarHeightMode === 'middle') this.switchMobileSidebarMode('full');
            if(this.mobileSidebarHeightMode === 'minimal') this.switchMobileSidebarMode('middle');
        },
        sidebarHeaderSwipeDownHandler() {
            this.clearMovingVariables();
            if(!this.mobile) return false;
            if(this.mobileSidebarHeightMode === 'middle') this.switchMobileSidebarMode('minimal')
            if(this.mobileSidebarHeightMode === 'full') this.switchMobileSidebarMode('middle');
        },
        showMobileSearchHandler(data) {
            this.showMobileSearchOverlay(data.waypointIndex, data.searchValue);
        },
        closeMobileSearchHandler() {
            this.closeMobileSearchOverlay();
        },
        sidebarToggleSwipeLeftHandler() {
            this.hideSidebar();
        },
        sidebarToggleSwipeRightHandler() {
            this.showSidebar();
        },
        sidebarHeaderMovingHandler(posY) {
            this.moveSidebarWithSwipe(posY);
        },
        showLocationPermissionMessageHandler(config = {}) {
            let descriptionText;
            if(config.general) {
                descriptionText = 'Um alle Funktionen dieser Webseite in vollem Umfang nutzen zu können, benötigt Ihr Browser Zugriff auf Ihren Standort. Die Erlaubnis können Sie jederzeit in Ihren Browsereinstellungen erteilen oder widerrufen.';
            } else {
                descriptionText = 'Um diese Funktion nutzen zu können, benötigt Ihr Browser Zugriff auf Ihren Standort. Die Erlaubnis können Sie jederzeit in Ihren Browsereinstellungen erteilen oder widerrufen.';
            }

            if(config === 'location') {
                this.showAlert(
                    'Zugriff auf Standort erlauben',
                    descriptionText,
                    'Verstanden',
                    () => { 
                        trackEvent({ category: 'Route Planner', action: 'Current Position', label: 'Accept' }) 
                        trackGa4Event({ga4Parameter1Name: 'options', ga4Parameter1Value: 'accept', ga4Event: 'current_position'})
                    }
                    // config.callback ? config.callback : false,
                );
            } else {
                this.showAlert(
                    'Zugriff auf Standort erlauben',
                    descriptionText,
                    'Verstanden',
                    config.callback ? config.callback : false,
                );
            }
        },
        showFavouriteRemovalConfirmationHandler(callback, type = 'favourite') {
            const texts = {
                title: 'Favorit wirklich löschen?',
                text: 'Möchten Sie den Favorit wirklich löschen?',
                accept: 'Verstanden',
                cancel: 'Abbrechen'
            }
            if(type === 'route') {
                texts.title = 'Möchten Sie die Route wirklich löschen?',
                texts.text = 'Die gespeicherte Route (inkl. Traffic Alert) wird endgültig gelöscht.'
                texts.accept = 'Löschen'
            }
            this.showConfirmation(
                texts.title,
                texts.text,
                texts.accept,
                texts.cancel,
                () => { callback() }
            );
        },
        showTrafficAlertPageHandler(routeId) {
            const url = `${window.location.origin}/vibs/default/alert/${routeId}`
            window.open(url, '_blank');
        },

        addListeners() {
            eventBus.$on('showMobileSearch', this.showMobileSearchHandler);
            eventBus.$on('closeMobileSearch', this.closeMobileSearchHandler);
            eventBus.$on('sidebarHeaderSwipeUp', this.sidebarHeaderSwipeUpHandler);
            eventBus.$on('sidebarHeaderSwipeDown', this.sidebarHeaderSwipeDownHandler);
            eventBus.$on('sidebarHeaderMoving', this.sidebarHeaderMovingHandler);
            eventBus.$on('showLocationPermissionMessage', this.showLocationPermissionMessageHandler);
            eventBus.$on('showFavouriteRemovalConfirmation', this.showFavouriteRemovalConfirmationHandler); // centralize favourite removal to  make it work with pin drop too
            eventBus.$on('showTrafficAlertPage', this.showTrafficAlertPageHandler)
        },
        removeListeners() {
            eventBus.$off('showMobileSearch', this.showMobileSearchHandler);
            eventBus.$off('closeMobileSearch', this.closeMobileSearchHandler);
            eventBus.$off('sidebarHeaderSwipeUp', this.sidebarHeaderSwipeUpHandler);
            eventBus.$off('sidebarHeaderSwipeDown', this.sidebarHeaderSwipeDownHandler);
            eventBus.$off('sidebarHeaderMoving', this.sidebarHeaderMovingHandler);
            eventBus.$off('showLocationPermissionMessage', this.showLocationPermissionMessageHandler);
            eventBus.$off('showFavouriteRemovalConfirmation', this.showFavouriteRemovalConfirmationHandler);
            eventBus.$off('showTrafficAlertPage', this.showTrafficAlertPageHandler)
        },
        switchMobileSidebarMode(mode) {
            this.$store.commit('setMobileSidebarHeightMode', mode)
            if(mode === 'minimal') eventBus.$emit('scrollSidebarToTop');
            this.sidebarMovingWithTransition = true;
            setTimeout(() => {
                this.sidebarMovingWithTransition = false;
            }, 400); // should match css transition value
        },
        showMobileSearchOverlay(waypointIndex, searchValue) {
            this.$store.commit('setMobileSearchVisible', true)
            this.mobileSearchWaypointIndex = waypointIndex;
            this.mobileSearchValue = searchValue;
        },
        closeMobileSearchOverlay() {
            this.$store.commit('setMobileSearchVisible', false)
        },
        toggleSidebar() {
            if(this.sidebarToggled) {
                this.showSidebar();
            } else {
                this.hideSidebar();
            }
        },
        hideSidebar() {
            this.sidebarToggled = true;
            eventBus.$emit('sidebarToggled', true);
        },
        showSidebar() {
            this.sidebarToggled = false;
            eventBus.$emit('sidebarToggled', false);
        },
        moveSidebarWithSwipe(posY) {
            if(!this.sidebarMovingPosition) {
                this.sidebarMovingPosition = posY;
                this.sidebarMovingWithTouch = true;
                return false;
            }
            let delta = this.sidebarMovingPosition - posY;
            this.sidebarMovingPosition = posY;
            this.moveMobileSidebarByPixels(delta);
        },
        moveMobileSidebarByPixels(delta) {
            this.mobileSidebarMovingDelta += delta;
        },
        clearMovingVariables() {
            this.sidebarMovingPosition = 0;
            this.mobileSidebarMovingDelta = 0;
            this.sidebarMovingWithTouch = false;
        },
    }
}
</script>

<style lang="scss">
.smr {
    $block: &;
    height: 100%;

    @include mq(tablet) {
        display: flex;
    }

    &__sidebar {
        z-index: 1;
        background-color: white;
        display: flex;
        flex-direction: column;
        position: relative;
        @include mq($until: tablet) {
            @include fillout;
            top: auto;
            z-index: 10;
            box-shadow: 0 -5.5px 12px 0 rgba(0, 0, 0, 0.15);
            transition: height 0.4s $ease-out-quart, border-radius 0.4s linear;
            border-radius: 9px 9px 0 0;
            overflow: hidden;
            min-height: 49px;
            .is-mobile-sidebar-size--full &{
                border-radius: 0;
            }
            .is-sidebar-moving-touch &{
                transition: none;
            }
        }
        @include mq(tablet) {
            position: relative;
            width: $sidebar-width;
        }
        @include mq($from: tablet) {
            transition: transform 0.6s $ease-out-quart;
            .is-sidebar-toggled &{
                transform: translateX(-#{$sidebar-width});
            }
        }

        &__inner {
            background-color: white;
            height: 100%;
            position: relative;
            z-index: 1;
            @include mq(tablet) {
                box-shadow: 0 2px 10px rgba(0,0,0,0.3);
            }
        }
        &__toggle {
            display: none;
            @include mq($from: tablet) {
                display: block;
                position: absolute;
                left: 100%;
                top: 50%;
                transform: translateY(-50%);
                width: 28px;
                height: 58px;
                margin-left: 0px;
                background-color: white;
                box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12), 0 6px 10px 0 rgba(0, 0, 0, 0.14);
                border-radius: 0 9px 9px 0;
                cursor: pointer;
                &__icon {
                    transition: transform 0.6s $ease-out-quart;
                    position: absolute;
                    top: 50%;
                    right: 7px;
                    transform: translateY(-50%);
                    color: $color-black;
                    .is-sidebar-toggled & {
                        transform: translateY(-50%) rotate(-180deg);
                    }
                }
            }
        }
    }

    &__map {
        z-index: 0;
        @include mq($until: tablet) {
            @include fillout;
            bottom: 49px;
        }
        @include mq(tablet) {
            position: relative;
            flex-grow: 1;
        }
    }
    &__mobile-search {
        @include fillout;
        z-index: 15;
        background-color: white;
        overflow: auto;
    }
}
</style>